import React, { Component } from 'react';
import PropTypes from 'prop-types';

import withMessages from 'component-utilities/intl';
import { formatDate, DATE_FORMATS } from 'component-utilities/dates/dates';

import filterStateLogic from './filterStateLogic';

import FormRow from './FormRow';
import SelectGenerator from '../../../Forms/Selects-v2/SelectGenerator';
import SelectMultiSelectGenerator from '../../../Forms/Selects-v2/SelectMultiSelectGenerator';
import InputFieldV2 from '../../../Forms/InputFields/InputFieldV2';
import CalendarSearch from '../../../Calendar/CalendarSearch';
import PrimaryButton from '../../../Buttons/PrimaryButton';
import SecondaryButton from '../../../Buttons/SecondaryButton';
import ButtonSpacer from '../../../Buttons/Spacer';

import { DATE_TYPES, ATTACHMENT_TYPES, SIZE_TYPES, TAG_TYPES, FORM_FIELDS } from '../config';

import styles from '../Filter.scss';

const SelectSmall = SelectGenerator('small', styles);
const SelectMedium = SelectGenerator('medium', styles);
const SelectMediumMultiSelect = SelectMultiSelectGenerator('medium', styles);

const { KEYWORDS, SENDER, PARTICIPANTS, DATE, SIZE, HOLD_REASON, ATTACHMENTS, TAG } = FORM_FIELDS;

const BASE_KEY = 'components.dropdowns.filter';

export default function DropdownFilterGenerator(type) {
  @withMessages
  @filterStateLogic({})
    class DropdownFilter extends Component {
      static propTypes = {
          getMessage: PropTypes.func.isRequired,
          formValues: PropTypes.object.isRequired,
          onFormChange: PropTypes.func.isRequired,
          holdReasons: PropTypes.array.isRequired,
          onSearch: PropTypes.func.isRequired,
          onCancel: PropTypes.func.isRequired,
          onClearAll: PropTypes.func.isRequired,
          onInputChange: PropTypes.func.isRequired,
          getLocalizedData: PropTypes.func.isRequired,
          isRangeDate: PropTypes.bool.isRequired,
          isFormHasValues: PropTypes.bool.isRequired,
          isValidForm: PropTypes.bool.isRequired,
          onMultiSelectChange: PropTypes.func.isRequired,
          isFieldEnabled: PropTypes.func.isRequired,
      };

      render() {
          const {
              holdReasons,
              getMessage,
              onClearAll,
              onSearch,
              onCancel,
              onInputChange,
              onFormChange,
              getLocalizedData,
              isRangeDate,
              isFormHasValues,
              isValidForm,
              formValues,
              onMultiSelectChange,
              isFieldEnabled,
          } = this.props;

          return (
              <div className={styles[`dropdown-filter${type ? `-${type}` : ''}`]}>
                  <div className={styles['dropdown-window-content']}>
                      <span className={styles['filter-tool']}>
                          {isFieldEnabled(KEYWORDS) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${KEYWORDS}`)}>
                                  <InputFieldV2
                                      focused
                                      data-field={KEYWORDS}
                                      onChange={({ target }) => onInputChange(KEYWORDS, target.value)}
                                      value={formValues[KEYWORDS].query}
                                  />
                              </FormRow>
                          )}
                          {isFieldEnabled(SENDER) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${SENDER}`)}>
                                  <InputFieldV2
                                      data-field={SENDER}
                                      onChange={({ target }) => onInputChange(SENDER, target.value)}
                                      value={formValues[SENDER].query}
                                  />
                              </FormRow>
                          )}
                          {isFieldEnabled(PARTICIPANTS) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${PARTICIPANTS}`)}>
                                  <InputFieldV2
                                      data-field={PARTICIPANTS}
                                      onChange={({ target }) => onInputChange(PARTICIPANTS, target.value)}
                                      value={formValues[PARTICIPANTS].query}
                                  />
                              </FormRow>
                          )}
                          {isFieldEnabled(DATE) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${DATE}`)}>
                                  <SelectSmall
                                      id="date_select"
                                      onSelect={onFormChange(DATE, 'type')}
                                      selectedId={formValues[DATE].type}
                                      data={getLocalizedData(DATE, DATE_TYPES)}
                                      placeholder={getMessage(`${BASE_KEY}.placeholders.dates`)}
                                  />
                                  {!!formValues[DATE].type && (
                                      <span className={styles['dropdown-calendar']}>
                                          <input
                                              type="text"
                                              className={styles['input-field']}
                                              name="start-date"
                                              maxLength={8}
                                              data-field={`${DATE}-from`}
                                              placeholder={isRangeDate ? 'Start date' : 'Date'}
                                              value={
                                                  formValues[DATE].from
                                                      ? formatDate(formValues[DATE].from, DATE_FORMATS.SHORT)
                                                      : ''
                                              }
                                              readOnly
                                          />
                                          <span className={styles['icon-calendar']} />
                                          <span className={styles['dropdown-box']}>
                                              <CalendarSearch
                                                  onDateSelected={onFormChange(DATE, 'from')}
                                                  selectedDate={formValues[DATE].from}
                                                  maxDate={isRangeDate ? formValues[DATE].to : null}
                                              />
                                          </span>
                                      </span>
                                  )}
                                  {isRangeDate && <span className={styles['date-range-to']}>
                                      to
                                  </span>}
                                  {isRangeDate && (
                                      <span className={styles['dropdown-calendar']}>
                                          <input
                                              type="text"
                                              className={styles['input-field']}
                                              name="end-date"
                                              maxLength={8}
                                              data-field={`${DATE}-to`}
                                              placeholder="End date"
                                              value={
                                                  formValues[DATE].to
                                                      ? formatDate(formValues[DATE].to, DATE_FORMATS.SHORT)
                                                      : ''
                                              }
                                              readOnly
                                          />
                                          <span className={styles['icon-calendar']} />
                                          <span className={styles['dropdown-box']}>
                                              <CalendarSearch
                                                  onDateSelected={onFormChange(DATE, 'to')}
                                                  selectedDate={formValues[DATE].to}
                                                  minDate={formValues[DATE].from}
                                              />
                                          </span>
                                      </span>
                                  )}
                              </FormRow>
                          )}
                          {isFieldEnabled(ATTACHMENTS) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${ATTACHMENTS}`)}>
                                  <div data-field={ATTACHMENTS}>
                                      <SelectMedium
                                          id="attachment-field"
                                          placeholder={getMessage(`${BASE_KEY}.placeholders.attachment`)}
                                          onSelect={onFormChange(ATTACHMENTS, 'query')}
                                          selectedId={formValues[ATTACHMENTS].query}
                                          data={getLocalizedData(ATTACHMENTS, ATTACHMENT_TYPES)}
                                      />
                                  </div>
                              </FormRow>
                          )}
                          {isFieldEnabled(SIZE) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${SIZE}`)}>
                                  <div data-field={SIZE}>
                                      <SelectMediumMultiSelect
                                          id="size-field"
                                          placeholder={getMessage(`${BASE_KEY}.placeholders.size`)}
                                          onSelect={onMultiSelectChange(SIZE, 'query')}
                                          selectedIds={formValues[SIZE].query || []}
                                          data={getLocalizedData(SIZE, SIZE_TYPES)}
                                      />
                                  </div>
                              </FormRow>
                          )}
                          {isFieldEnabled(HOLD_REASON) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${HOLD_REASON}`)}>
                                  <div data-field={HOLD_REASON}>
                                      <SelectMediumMultiSelect
                                          id="hold-reasons-field"
                                          placeholder={getMessage(`${BASE_KEY}.placeholders.hold_reason`)}
                                          onSelect={onMultiSelectChange(HOLD_REASON, 'query')}
                                          selectedIds={formValues[HOLD_REASON].query || []}
                                          data={holdReasons}
                                      />
                                  </div>
                              </FormRow>
                          )}
                          {isFieldEnabled(TAG) && (
                              <FormRow label={getMessage(`${BASE_KEY}.labels.${TAG}`)}>
                                  <div data-field={TAG}>
                                      <SelectMediumMultiSelect
                                          id="tag-field"
                                          placeholder={getMessage(`${BASE_KEY}.placeholders.tag`)}
                                          onSelect={onMultiSelectChange(TAG, 'query')}
                                          selectedIds={formValues[TAG].query || []}
                                          data={getLocalizedData(TAG, TAG_TYPES)}
                                      />
                                  </div>
                              </FormRow>
                          )}
                      </span>
                  </div>
                  <div className={styles['dropdown-window-footer']}>
                      <div className={styles['dropdown-buttons']}>
                          <div className={styles['dropdown-button-right']}>
                              <SecondaryButton
                                  onClick={onClearAll}
                                  disabled={!isFormHasValues}
                                  data-action="clear-all"
                              >
                                  {getMessage(`${BASE_KEY}.buttons.clear_all`)}
                              </SecondaryButton>
                          </div>
                          <PrimaryButton onClick={onSearch} disabled={!isValidForm} data-action="apply">
                              {getMessage(`${BASE_KEY}.buttons.apply`)}
                          </PrimaryButton>
                          <ButtonSpacer />
                          <SecondaryButton onClick={onCancel} data-action="cancel">
                              {getMessage(`${BASE_KEY}.buttons.cancel`)}
                          </SecondaryButton>
                      </div>
                  </div>
              </div>
          );
      }
  }

  return DropdownFilter;
}
